import React, { useState, useEffect } from 'react';
import { createPPTFromReportData } from './ppt_parser';
import * as XLSX from 'xlsx';
import './MonthlyReport.css';

const MonthlyReport = ({ currentUser }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    // Load report data when component mounts or selected month changes
    if (selectedMonth) {
      loadReportData();
    }
  }, [selectedMonth]);

  const loadReportData = () => {
    setLoading(true);
    try {
      // Get call logs from localStorage
      const savedCallLogs = localStorage.getItem('bme-call-logs');
      if (savedCallLogs) {
        const allLogs = JSON.parse(savedCallLogs);
        
        // Filter logs for selected month
        const monthLogs = allLogs.filter(log => {
          if (!log.date) return false;
          // Extract year-month from log date (YYYY-MM-DD format)
          const logMonth = log.date.slice(0, 7);
          return logMonth === selectedMonth;
        });
        
        // Calculate report statistics
        const report = calculateReportStats(monthLogs);
        setReportData(report);
      } else {
        setReportData(null);
      }
    } catch (error) {
      console.error('Failed to load report data:', error);
      showMessage('Failed to load report data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const calculateReportStats = (logs) => {
    // Total calls in the month
    const totalCalls = logs.length;
    
    // Department-wise breakdown
    const departmentStats = {};
    logs.forEach(log => {
      const dept = log.department || 'Unknown';
      departmentStats[dept] = (departmentStats[dept] || 0) + 1;
    });
    
    // Status-wise breakdown
    const statusStats = {};
    logs.forEach(log => {
      const status = log.callStatus || 'Unknown';
      statusStats[status] = (statusStats[status] || 0) + 1;
    });
    
    // Type-wise breakdown
    const typeStats = {};
    logs.forEach(log => {
      const type = log.type || 'BME';
      typeStats[type] = (typeStats[type] || 0) + 1;
    });
    
    // Calculate average response time (in minutes)
    let totalResponseTime = 0;
    let validResponseTimes = 0;
    
    logs.forEach(log => {
      if (log.responseTime) {
        // Parse response time (assuming format "HH:MM" or just minutes)
        const timeParts = log.responseTime.split(':');
        if (timeParts.length === 2) {
          // Format is HH:MM
          const hours = parseInt(timeParts[0]) || 0;
          const minutes = parseInt(timeParts[1]) || 0;
          totalResponseTime += (hours * 60) + minutes;
          validResponseTimes++;
        } else if (timeParts.length === 1) {
          // Format is just minutes
          const minutes = parseInt(timeParts[0]) || 0;
          totalResponseTime += minutes;
          validResponseTimes++;
        }
      }
    });
    
    const avgResponseTime = validResponseTimes > 0 ? 
      (totalResponseTime / validResponseTimes).toFixed(2) : 'N/A';
    
    // Calculate average resolution time (in minutes)
    let totalResolutionTime = 0;
    let validResolutionTimes = 0;
    
    logs.forEach(log => {
      if (log.resolutionTime) {
        // Parse resolution time (assuming format "HH:MM")
        const timeParts = log.resolutionTime.split(':');
        if (timeParts.length === 2) {
          const hours = parseInt(timeParts[0]) || 0;
          const minutes = parseInt(timeParts[1]) || 0;
          totalResolutionTime += (hours * 60) + minutes;
          validResolutionTimes++;
        }
      }
    });
    
    const avgResolutionTime = validResolutionTimes > 0 ? 
      (totalResolutionTime / validResolutionTimes).toFixed(2) : 'N/A';
    
    // Top 5 equipment with most breakdowns
    const equipmentBreakdown = {};
    logs.forEach(log => {
      const equipment = log.equipmentName || 'Unknown Equipment';
      equipmentBreakdown[equipment] = (equipmentBreakdown[equipment] || 0) + 1;
    });
    
    const topEquipment = Object.entries(equipmentBreakdown)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([equipment, count]) => ({ equipment, count }));
    
    return {
      totalCalls,
      departmentStats,
      statusStats,
      typeStats,
      avgResponseTime,
      avgResolutionTime,
      topEquipment,
      logs
    };
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  const exportReport = (format) => {
    if (!reportData) {
      showMessage('No report data to export', 'error');
      return;
    }
    
    try {
      if (format === 'csv') {
        exportToCSV();
      } else if (format === 'excel') {
        exportToExcel();
      } else if (format === 'ppt') {
        exportToPPT();
      }
    } catch (error) {
      console.error('Failed to export report:', error);
      showMessage('Failed to export report', 'error');
    }
  };

  const exportToCSV = () => {
    // Create CSV content
    let csvContent = 'Monthly Report\n';
    csvContent += `Month: ${selectedMonth}\n\n`;
    
    csvContent += 'Summary\n';
    csvContent += 'Metric,Value\n';
    csvContent += `Total Calls,${reportData.totalCalls}\n`;
    csvContent += `Average Response Time (minutes),${reportData.avgResponseTime}\n`;
    csvContent += `Average Resolution Time (minutes),${reportData.avgResolutionTime}\n\n`;
    
    csvContent += 'Department-wise Breakdown\n';
    csvContent += 'Department,Count\n';
    Object.entries(reportData.departmentStats).forEach(([dept, count]) => {
      csvContent += `${dept},${count}\n`;
    });
    
    csvContent += '\nStatus-wise Breakdown\n';
    csvContent += 'Status,Count\n';
    Object.entries(reportData.statusStats).forEach(([status, count]) => {
      csvContent += `${status},${count}\n`;
    });
    
    csvContent += '\nType-wise Breakdown\n';
    csvContent += 'Type,Count\n';
    Object.entries(reportData.typeStats).forEach(([type, count]) => {
      csvContent += `${type},${count}\n`;
    });
    
    csvContent += '\nTop Equipment Breakdowns\n';
    csvContent += 'Equipment,Count\n';
    reportData.topEquipment.forEach(item => {
      csvContent += `${item.equipment},${item.count}\n`;
    });
    
    // Create download link
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `monthly_report_${selectedMonth}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showMessage('Report exported to CSV successfully!', 'success');
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ['Monthly Report', selectedMonth],
      [],
      ['Metric', 'Value'],
      ['Total Calls', reportData.totalCalls],
      ['Avg Response Time (min)', reportData.avgResponseTime],
      ['Avg Resolution Time (min)', reportData.avgResolutionTime],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryData), 'Summary');

    // Department sheet
    const deptData = [['Department', 'Count'], ...Object.entries(reportData.departmentStats || {})];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(deptData), 'By Department');

    // Status sheet
    const statusData = [['Status', 'Count'], ...Object.entries(reportData.statusStats || {})];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(statusData), 'By Status');

    // Top Equipment sheet
    const equipData = [['Equipment', 'Count'], ...(reportData.topEquipment || []).map(e => [e.equipment, e.count])];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(equipData), 'Top Equipment');

    XLSX.writeFile(wb, `monthly_report_${selectedMonth}.xlsx`);
    showMessage('Report exported to Excel successfully!', 'success');
  };

  const exportToPPT = async () => {
    try {
      const pptBlob = await createPPTFromReportData(reportData, selectedMonth);
      
      // Create download link
      const url = URL.createObjectURL(pptBlob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `monthly_report_${selectedMonth}.pptx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showMessage('Report exported to PowerPoint successfully!', 'success');
    } catch (error) {
      console.error('Failed to export report to PPT:', error);
      showMessage(`Failed to export report to PowerPoint: ${error.message}`, 'error');
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long' };
    const date = new Date(dateString + '-01');
    return date.toLocaleDateString(undefined, options);
  };

  return (
    <div className="monthly-report-container">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">📅 Monthly Report</h2>
        </div>
        
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? '✅' : '❌'} {message.text}
          </div>
        )}
        
        <div className="report-controls">
          <div className="form-group">
            <label htmlFor="report-month">Select Month:</label>
            <input
              type="month"
              id="report-month"
              value={selectedMonth}
              onChange={handleMonthChange}
              max={new Date().toISOString().slice(0, 7)}
            />
          </div>
          <div className="export-buttons">
            <button className="btn btn-info" onClick={() => exportReport('csv')}>
              📤 Export to CSV
            </button>
            <button className="btn btn-success" onClick={() => exportReport('excel')}>
              📊 Export to Excel
            </button>
            <button className="btn btn-warning" onClick={() => exportReport('ppt')}>
              📽️ Export to PowerPoint
            </button>
          </div>
        </div>
        
        {loading ? (
          <div className="loading">Loading report data...</div>
        ) : reportData ? (
          <div className="report-content">
            <h3>Report for {formatDate(selectedMonth)}</h3>
            
            <div className="report-summary">
              <div className="summary-card">
                <h4>Total Service Calls</h4>
                <div className="summary-value">{reportData.totalCalls}</div>
              </div>
              
              <div className="summary-card">
                <h4>Avg Response Time</h4>
                <div className="summary-value">
                  {reportData.avgResponseTime !== 'N/A' 
                    ? `${reportData.avgResponseTime} minutes` 
                    : 'N/A'}
                </div>
              </div>
              
              <div className="summary-card">
                <h4>Avg Resolution Time</h4>
                <div className="summary-value">
                  {reportData.avgResolutionTime !== 'N/A' 
                    ? `${reportData.avgResolutionTime} minutes` 
                    : 'N/A'}
                </div>
              </div>
            </div>
            
            <div className="report-sections">
              <div className="report-section">
                <h4>Department-wise Breakdown</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Department</th>
                      <th>Number of Calls</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(reportData.departmentStats)
                      .sort((a, b) => b[1] - a[1])
                      .map(([dept, count]) => {
                        const percentage = ((count / reportData.totalCalls) * 100).toFixed(1);
                        return (
                          <tr key={dept}>
                            <td>{dept}</td>
                            <td>{count}</td>
                            <td>{percentage}%</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
              
              <div className="report-section">
                <h4>Status-wise Breakdown</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Number of Calls</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(reportData.statusStats)
                      .sort((a, b) => b[1] - a[1])
                      .map(([status, count]) => {
                        const percentage = ((count / reportData.totalCalls) * 100).toFixed(1);
                        return (
                          <tr key={status}>
                            <td>{status}</td>
                            <td>{count}</td>
                            <td>{percentage}%</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
              
              <div className="report-section">
                <h4>Type-wise Breakdown</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Number of Calls</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(reportData.typeStats)
                      .sort((a, b) => b[1] - a[1])
                      .map(([type, count]) => {
                        const percentage = ((count / reportData.totalCalls) * 100).toFixed(1);
                        return (
                          <tr key={type}>
                            <td>{type}</td>
                            <td>{count}</td>
                            <td>{percentage}%</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
              
              <div className="report-section">
                <h4>Top Equipment Breakdowns</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Equipment</th>
                      <th>Number of Breakdowns</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.topEquipment.map((item, index) => (
                      <tr key={index}>
                        <td>{item.equipment}</td>
                        <td>{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="no-data">
            No report data available for the selected month. Please select a different month or 
            ensure that call log data exists for this period.
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyReport;